// @ts-strict-ignore
import { cssVar, getLuminance } from 'polished';
import type { FeatureFlags } from '~/helpers/typescript';
import { EDITOR_2_ON_LOCAL_STORAGE_KEY, EDITOR_2_OFF_LOCAL_STORAGE_KEY } from '~/constants';
import {
  setLocalStorageValue,
  removeLocalStorageValue,
  getLocalStorageValue,
} from '~/helpers/localStorage';
import type { DropdownOption } from './NodeEditorDrawer/types';
import type { UserContextValue } from '~/dashboard/context';
import { libraryRoutes } from '~/helpers/api-new/modules/library/libraryRoutes';
import { SNIPPET_SCOPE } from './MenuBar/MenuBarModals/PolicyLibraryModal/constants';
import type { SnippetScope } from './MenuBar/MenuBarModals/PolicyLibraryModal/constants';

/**
 * Determines the snippet scope based on the URL pathname.
 * Returns 'local' for local snippets, 'global' for global snippets, or null for regular workflows.
 */
export function getSnippetScopeFromPath(pathname: string): SnippetScope | null {
  if (pathname.includes(libraryRoutes.localSnippetsView)) return SNIPPET_SCOPE.LOCAL;
  if (pathname.includes(libraryRoutes.globalRepository.view)) return SNIPPET_SCOPE.GLOBAL;
  return null;
}


export function capitalizeString(text: string): string {
  return text[0].toUpperCase() + text.slice(1);
}

/**
 * helper to format a color string to a format CSS understands.
 * mostly used to convert from formats like "FC6655" to "#FC6655"
 * @param color
 * @returns color in a CSS-compatible color
 */
export function formatColor(color: string): string {
  if (!color.includes('var(') && color.charAt(0) !== '#') {
    return `#${color}`;
  }

  return color;
}

export function getColorFromVariable(color: string): string {
  if (color.includes('var(')) {
    return `${cssVar(color.replace(/var\((.+)\)/, '$1'))}`;
  }

  return formatColor(color);
}

export function getIconColorForBackgroundColor(backgroundColor: string): string {
  // This shouldn't really happen but can be useful for workflows with
  // corrupted outcome reasons - it will display without crashing the page.
  if (!backgroundColor) {
    return '';
  }
  // 0.2 is sort-of a magic number
  return getLuminance(getColorFromVariable(backgroundColor)) >= 0.2 ? 'black' : 'white';
}

// There are two feature flags and two localStorage items at play here.
// The feature flags are:
// - on-by-default-workflow-editor-v2
// - workflow-editor-v2

// The localStorage items are:
// - EDITOR_2_OFF_LOCAL_STORAGE_KEY ('workflow-editor-v2-turn-off')
// - EDITOR_2_ON_LOCAL_STORAGE_KEY ('workflow-editor-v2-redirect')

// The behavior here is reversed for each flag.
// For 'on-by-default-workflow-editor-v2' (takes precedence):
// - If the flag is on, the editor is on by default.
// - We let users toggle the editor off by setting EDITOR_2_OFF_LOCAL_STORAGE_KEY
// - on their local storage, but only for 24 hours.
// - Then, the editor is turned back on and localStorage is cleared.
// For 'workflow-editor-v2' (fallback):
// - If the flag is on, the editor is off by default.
// - We let users toggle the editor on by setting EDITOR_2_ON_LOCAL_STORAGE_KEY to 'true',
// - which keeps the editor on until the user toggles it off again.
// - When they toggle it off, we remove the localStorage item.

export function toggleEditorV2Off(featureFlags?: Partial<FeatureFlags>): void {
  if (!window.localStorage) {
    return;
  }
  if (featureFlags?.onByDefaultWorkflowEditorV2) {
    setLocalStorageValue(EDITOR_2_OFF_LOCAL_STORAGE_KEY, 'true', 24);
  } else {
    removeLocalStorageValue(EDITOR_2_ON_LOCAL_STORAGE_KEY);
  }
}

export function toggleEditorV2On(featureFlags?: Partial<FeatureFlags>): void {
  if (!window.localStorage) {
    return;
  }
  if (featureFlags?.['on-by-default-workflow-editor-v2']) {
    removeLocalStorageValue(EDITOR_2_OFF_LOCAL_STORAGE_KEY);
  } else {
    setLocalStorageValue(EDITOR_2_ON_LOCAL_STORAGE_KEY, 'true');
  }
}

export function isEditorV2On(featureFlags?: Partial<FeatureFlags>): boolean {
  if (!window.localStorage) {
    return false;
  }
  if (featureFlags?.workflowEditorV2Ga) {
    return true;
  }
  if (featureFlags?.onByDefaultWorkflowEditorV2) {
    return getLocalStorageValue(EDITOR_2_OFF_LOCAL_STORAGE_KEY) !== 'true';
  }
  if (featureFlags?.workflowEditorV2) {
    return getLocalStorageValue(EDITOR_2_ON_LOCAL_STORAGE_KEY) === 'true';
  }
  return false;
}


/**
 * this helper is related to rule/expression building and should eventually be moved to
 * common abstraction for threshold, expression and mla building.
 */
export function getSelectedOption({
  incomingValue, options,
}: {
  incomingValue: string;
  options: DropdownOption[];
}) {
  let matchingOptionItem: DropdownOption;
  options.some(item => {
    if (item.value === incomingValue) {
      matchingOptionItem = item;
      return true;
    }

    if (item.options) {
      const matchingNestedValue = item.options.find(
        nestedValue => nestedValue.value === incomingValue);
      if (matchingNestedValue) {
        matchingOptionItem = matchingNestedValue;
        return true;
      }
    }

    return false;
  });

  return matchingOptionItem;
}

/**
 * this helper is related to rule/expression building and should eventually be moved to
 * common abstraction for threshold, expression and mla building.
 */
export const onEscKeyFromRuleDropdown = (
  e: React.KeyboardEvent<HTMLInputElement>,
  activeInputValue: {
    value: string;
    path: string;
    initialValue: boolean;
  },
  setActiveInputValue: ({
    value,
    path,
    initialValue,
  }) => void,
  multiSelectRef: React.MutableRefObject<any>,
) => {
  if (activeInputValue.value !== '') {
    setActiveInputValue({
      value: '',
      path: '',
      initialValue: false,
    });
  } else {
    multiSelectRef?.current?.blur();
  }

  e.preventDefault();
  e.stopPropagation();
};

export function isInSnippetEditor(breadcrumbs: UserContextValue['breadcrumbs']): boolean {
  // Check for both global snippets (Policy Library) and local snippets
  const snippetEditorPaths = [
    libraryRoutes.globalRepository.view,
    '/v3/dashboard/workflows?tab=snippets',
  ];
  return breadcrumbs?.some(
    crumb => crumb.href && snippetEditorPaths.some(path => crumb.href?.includes(path)),
  );
}
